import * as React from 'react';
import styled from 'styled-components';
import { DataCy } from '../../types/dataAttributes';
import { Button, ButtonColors } from '../JRCButton/JRCButton';
import { BUTTON_CONFIG } from '../JRCButton/JRCButtonConfig';
import { Link } from 'react-router-dom';
import { JRCConditionalTooltip } from '../Common/JRCConditionalWrapper';

/**
 * Props type for JRCLinkToArticle and JRCLinkToUser
 * @member idObject number : id of the record
 */
export type JRCLinkToProps = DataCy &
    React.ComponentPropsWithoutRef<'a'> & {
        idObject?: number;
    };

type NativeProps = React.ComponentPropsWithoutRef<'a'> & {
    useLink?: boolean;
    hasLicense?: boolean;
};

type AnchorProps = {
    as?: 'a';
};

type ButtonProps = {
    variant?: 'contained' | 'outlined';
    color?: ButtonColors;
    float?: 'left' | 'right';
    disabled?: boolean;
    children?: React.ReactNode;
    minWidth?: string;
    noMargin?: boolean;
    hasLabel?: boolean;
    as: 'button';
};

/**
 * Props type for JRCStyledHref
 * @member as render the link styled as a button
 */
export type JRCStyledHrefProps = DataCy & (AnchorProps | ButtonProps) & NativeProps;

const Href = styled.a.attrs<DataCy>(({ dataCy, ...props }) => ({
    'data-cy': dataCy,
    ...props,
}))`
    color: ${(props) => props.theme.font.hrefColor};
    :focus {
        text-decoration: underline;
    }
`;

export const JRCStyledHref = ({ dataCy, hasLicense = true, href, useLink, ...props }: JRCStyledHrefProps) => {
    const element = props.as === 'button' ? Button : Href;

    const disabled = ('disabled' in props && props.disabled) || !hasLicense;

    const hrefProps = useLink
        ? {
              as: Link,
              to: disabled ? undefined : href,
          }
        : {
              as: 'a',
              href: disabled ? undefined : href,
          };

    const buttonProps =
        props.as === 'button'
            ? {
                  $themeButton:
                      BUTTON_CONFIG[(props.variant || 'contained') as 'contained'][
                          disabled ? 'disabled' : props.color || 'primary'
                      ],
                  disabled,
              }
            : undefined;

    return (
        <JRCConditionalTooltip hasLicense={hasLicense}>
            {React.createElement(element, { ...props, ...hrefProps, ...buttonProps, 'data-cy': dataCy })}
        </JRCConditionalTooltip>
    );
};
